import React, { useCallback, useEffect, useRef, useState } from "react";
import { Empty, Input, Modal, Radio, Spin, Tag, Typography } from "@douyinfe/semi-ui";
import { IconChevronRight, IconSearch } from "@douyinfe/semi-icons";
import {
  AiCoachChatApi,
  ChatCustomer,
  LeadListType,
} from "../../../api/aiCoachChat";
import { intentionLevelLabel } from "../../../api/aiCoach";
import { displayLeadStatus } from "../../../api/ApiClient";
import {
  avatar,
  customerListBody,
  customerListCard,
  customerRowItem,
  panelHeader,
  scoreBadgeColor,
  scoreBadgeStyle,
  sp,
} from "../styles";

const { Text, Title } = Typography;

interface Props {
  selectedId: number | null;
  onSelect: (c: ChatCustomer) => void;
}

const TYPE_OPTIONS: Array<{ value: LeadListType; label: string }> = [
  { value: "new", label: "我的客户" },
  { value: "my", label: "再分配" },
  { value: "fav", label: "重要客户" },
  { value: "team", label: "团队客户" },
];

const PAGE_SIZE = 20;

const AVATAR_COLORS = [
  "#6C5CE7", "#00B894", "#0984E3", "#E17055", "#00CEC9",
  "#FDCB6E", "#E84393", "#636E72", "#2D3436", "#74B9FF",
];

function colorFor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function initialOf(name?: string): string {
  if (!name) return "?";
  return name.trim().charAt(0).toUpperCase();
}

function leadToCustomer(l: any): ChatCustomer {
  return {
    id: l.id,
    name: l.name,
    leadNo: l.leadNo,
    star: l.star,
    intentionLevel: l.intentionLevel,
    status: typeof l.status === "string" ? l.status : (l.status as any)?.name,
    score: l.score,
    fullMobile: l.fullMobile,
    applyCity: l.applyCity,
    applyAmount: l.applyAmount,
  };
}

const CustomerListPanel: React.FC<Props> = ({ selectedId, onSelect }) => {
  const [type, setType] = useState<LeadListType>("new");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [list, setList] = useState<ChatCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<{ customer: ChatCustomer; userPrompt: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(async (p: number, kw: string, t: LeadListType, append: boolean) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const r = await AiCoachChatApi.listLeads({ type: t, page: p, size: PAGE_SIZE, keyword: kw || undefined });
      const records = r.data?.records ?? [];
      setTotal(r.data?.total ?? 0);
      setList((prev) => append ? [...prev, ...records.map(leadToCustomer)] : records.map(leadToCustomer));
      setPage(p);
    } catch {
      if (!append) setList([]);
    } finally {
      setLoadingMore(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchPage(1, keyword, type, false), 350);
    return () => clearTimeout(t);
  }, [keyword, type, fetchPage]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom && !loadingMore && !loading && list.length < total) {
      fetchPage(page + 1, keyword, type, true);
    }
  };

  const openDetail = async (c: ChatCustomer) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await AiCoachChatApi.userPrompt(c.id);
      setDetail({ customer: c, userPrompt: r.data ?? "" });
    } catch {
      setDetail({ customer: c, userPrompt: "" });
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div style={customerListCard}>
      <div style={{ ...panelHeader, paddingBottom: sp.xs, borderBottom: "none" }}>
        <Title heading={6} style={{ margin: 0 }}>客户列表</Title>
        {!loading && total > 0 && (
          <Text type="tertiary" size="small">共 {total} 位</Text>
        )}
      </div>

      <div style={{}}>
        <Radio.Group
          type="button"
          buttonSize="middle"
          value={type}
          onChange={(e) => setType(e.target.value as LeadListType)}
          options={TYPE_OPTIONS}
          style={{ width: "100%" }}
        />
      </div>

      <div style={{ padding: `0 ${sp.sm} ${sp.sm}`, flexShrink: 0 }}>
        <Input
          placeholder="搜索姓名 / 手机号"
          value={keyword}
          onChange={setKeyword}
          showClear
          prefix={<IconSearch style={{ color: "var(--semi-color-text-2)" }} />}
        />
      </div>

      <div ref={scrollRef} style={customerListBody} onScroll={handleScroll}>
        {loading ? (
          <div style={{ textAlign: "center", padding: sp.xl }}><Spin /></div>
        ) : list.length === 0 ? (
          <Empty description="未找到客户" style={{ padding: 40 }} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: sp.xs }}>
            {list.map((c) => {
              const selected = selectedId === c.id;
              return (
                <div
                  key={c.id}
                  onClick={() => onSelect(c)}
                  style={{
                    ...customerRowItem,
                    background: selected
                      ? "var(--semi-color-primary-light-default)"
                      : "var(--semi-color-bg-2)",
                    borderColor: selected ? "var(--semi-color-primary)" : "rgba(var(--semi-grey-1), 1)",
                   
                  }}
                  onMouseEnter={(e) => {
                    if (!selected) e.currentTarget.style.background = "var(--semi-color-fill-0)";
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) e.currentTarget.style.background = "var(--semi-color-bg-2)";
                  }}
                >
                  {/* <div
                    style={{
                      ...avatar,
                      background: colorFor(c.id),
                      boxShadow: selected
                        ? "0 0 0 2px var(--semi-color-primary-light-active)"
                        : "none",
                    }}
                  >
                    {initialOf(c.name)}
                  </div> */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: sp.xs }}>
                      <Text strong ellipsis={{ showTooltip: { opts: { content: c.name } } }} style={{ maxWidth: 100 }}>
                        {c.name || `#${c.id}`}
                      </Text>
                      {c.score != null && (
                        <span
                          style={{ ...scoreBadgeStyle, color: scoreBadgeColor(c.score) }}
                          title="AI 评分"
                        >
                          {c.score}分
                        </span>
                      )}
                      {c.star != null && (
                        <Text type="warning" size="small">{c.star}星</Text>
                      )}
                      {c.applyAmount != null && (
                        <Text  size="small">{c.applyAmount}</Text>
                      )}
                    </div>
                    <div style={{ marginTop: 2, display: "flex", alignItems: "center", gap: sp.xs }}>
                      {c.intentionLevel && (
                        <Text type="tertiary" size="small">
                          {intentionLevelLabel(c.intentionLevel)}
                        </Text>
                      )}
                      {c.status && (
                        <Tag size="small" color="blue">{displayLeadStatus(c.status)}</Tag>
                      )}
                    </div>
                  </div>
                  <IconChevronRight
                    style={{ color: "var(--semi-color-text-2)", flexShrink: 0, fontSize: 14, cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDetail(c);
                    }}
                  ></IconChevronRight>
                </div>
              );
            })}

            {loadingMore && (
              <div style={{ textAlign: "center", padding: sp.sm }}>
                <Spin />
              </div>
            )}
            {!loadingMore && list.length >= total && list.length > 0 && (
              <div style={{ textAlign: "center", padding: sp.sm }}>
                <Text type="tertiary" size="small">— 已加载全部 {total} 位 —</Text>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        title={detail?.customer ? `客户详情 · ${detail.customer.name || `#${detail.customer.id}`}` : "客户详情"}
        visible={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={520}
      >
        {detailLoading ? (
          <div style={{ textAlign: "center", padding: sp.xl }}><Spin /></div>
        ) : detail ? (
          <DetailBody customer={detail.customer} userPrompt={detail.userPrompt} />
        ) : null}
      </Modal>
    </div>
  );
};

const DetailBody: React.FC<{ customer: ChatCustomer; userPrompt: string }> = ({ customer, userPrompt }) => {
  const rows: Array<[string, React.ReactNode]> = [
    ["客户编号", customer.leadNo || `#${customer.id}`],
    ["姓名", customer.name || "-"],
    ["手机号", customer.fullMobile || "-"],
    ["城市", customer.applyCity || "-"],
    ["意向", customer.intentionLevel ? `${intentionLevelLabel(customer.intentionLevel)} (${customer.intentionLevel})` : "-"],
    ["AI 评分", customer.score != null ? `${customer.score} 分` : "-"],
    ["星级", customer.star != null ? `${customer.star} ★` : "-"],
    ["状态", customer.status ? displayLeadStatus(customer.status) : "-"],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: sp.md }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm }}>
        {rows.map(([label, value]) => (
          <div
            key={label}
            style={{
              padding: `${sp.xs} ${sp.sm}`,
              background: "var(--semi-color-bg-2)",
              borderRadius: 6,
            }}
          >
            <div>
              <Text type="tertiary" size="small">{label}</Text>
            </div>
            <div style={{ marginTop: 2 }}>
              <Text strong>{value}</Text>
            </div>
          </div>
        ))}
      </div>

      {userPrompt && (
        <div>
          <Text type="tertiary" size="small" strong>注入给 AI 的客户上下文</Text>
          <pre
            style={{
              marginTop: sp.xs,
              padding: sp.sm,
              background: "var(--semi-color-bg-2)",
              borderRadius: 6,
              fontSize: 12,
              lineHeight: 1.6,
              maxHeight: 260,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: 0,
            }}
          >
            {userPrompt}
          </pre>
        </div>
      )}
    </div>
  );
};

export default CustomerListPanel;
